/**
 * Notes lock credential handling.
 *
 * The PIN is never held in state or storage — only a salted digest of it is.
 * In production this digest would be produced and verified server-side with a
 * slow KDF; the shape of the flow (salt, digest, throttling, expiring reset
 * code) is what matters here, and the client never gets to decide the answer
 * on its own.
 */

const MAX_ATTEMPTS = 5
const COOLDOWN_SECONDS = 60
const RESET_CODE_TTL_SECONDS = 600

export interface LockCredential {
  salt: string
  digest: string
  setOn: string
  /** Consecutive failures since the last success. */
  failedAttempts: number
  /** Epoch ms until which entry is refused, or null. */
  cooldownUntil: number | null
}

export interface ResetChallenge {
  /** The code that was "sent" — never shown in the UI in production. */
  code: string
  sentTo: string
  expiresAt: number
  attempts: number
}

export function randomSalt() {
  return Math.random().toString(36).slice(2, 10)
}

/** FNV-1a over salt + pin. Deterministic, and the PIN is not recoverable from it. */
export function digestPin(pin: string, salt: string) {
  let hash = 0x811c9dc5
  const input = `${salt}:${pin}`
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

export function createCredential(pin: string, setOn: string): LockCredential {
  const salt = randomSalt()
  return { salt, digest: digestPin(pin, salt), setOn, failedAttempts: 0, cooldownUntil: null }
}

export function verifyPin(credential: LockCredential, pin: string) {
  return digestPin(pin, credential.salt) === credential.digest
}

/** Reject PINs that offer no real protection. */
export function pinProblem(pin: string): string | null {
  if (!/^\d{4,6}$/.test(pin)) return 'Use 4 to 6 digits.'
  if (/^(\d)\1+$/.test(pin)) return 'Avoid a single repeated digit.'
  const ascending = '0123456789'
  const descending = '9876543210'
  if (ascending.includes(pin) || descending.includes(pin)) {
    return 'Avoid digits in sequence.'
  }
  return null
}

export function cooldownRemaining(credential: LockCredential | null) {
  if (!credential?.cooldownUntil) return 0
  return Math.max(0, Math.ceil((credential.cooldownUntil - Date.now()) / 1000))
}

export function attemptsLeft(credential: LockCredential | null) {
  if (!credential) return MAX_ATTEMPTS
  return Math.max(0, MAX_ATTEMPTS - credential.failedAttempts)
}

export function registerFailure(credential: LockCredential): LockCredential {
  const failedAttempts = credential.failedAttempts + 1
  return {
    ...credential,
    failedAttempts,
    cooldownUntil:
      failedAttempts >= MAX_ATTEMPTS ? Date.now() + COOLDOWN_SECONDS * 1000 : credential.cooldownUntil,
  }
}

export function clearFailures(credential: LockCredential): LockCredential {
  return { ...credential, failedAttempts: 0, cooldownUntil: null }
}

export function issueResetChallenge(email: string): ResetChallenge {
  return {
    code: String(Math.floor(100000 + Math.random() * 900000)),
    sentTo: email,
    expiresAt: Date.now() + RESET_CODE_TTL_SECONDS * 1000,
    attempts: 0,
  }
}

export function challengeExpired(challenge: ResetChallenge) {
  return Date.now() > challenge.expiresAt
}

export function challengeMinutesLeft(challenge: ResetChallenge) {
  return Math.max(0, Math.ceil((challenge.expiresAt - Date.now()) / 60000))
}

/** amarachi.obi@campuzon.com → a•••••••••bi@campuzon.com */
export function maskEmail(email: string) {
  const [local, domain] = email.split('@')
  if (!domain) return email
  if (local.length <= 3) return `${local[0]}•••@${domain}`
  return `${local[0]}${'•'.repeat(Math.max(3, local.length - 3))}${local.slice(-2)}@${domain}`
}

export { MAX_ATTEMPTS, COOLDOWN_SECONDS }
